import Sheet from 'components/utils/Sheet'
import { useCallback, useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import useMerchandiseSearchFacets from 'hooks/Merchandise/useMerchandiseSearchFacets'
import zIndex from 'styles/tools/z-index'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import MerchandiseCategoryMegaMenu from '../MerchandiseMegaMenu/MerchandiseCategoryMenu'
import MerchandiseBrandsMegaMenu from '../MerchandiseMegaMenu/MerchandiseBrandsMenu'
import MerchandiseTravelEssentialsMegaMenu from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsMenu'
import { MerchandiseNavBarButton, MerchandiseNavBarFilterChip } from './MerchandiseNavBarButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import MerchandiseCategoryDrawer from '../MerchandiseMegaMenu/MerchandiseCategoryDrawer'
import MerchandiseBrandsDrawer from '../MerchandiseMegaMenu/MerchandiseBrandsDrawer'
import MerchandiseTravelEssentialsDrawer from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsDrawer'
import type { MegaMenuSection } from 'analytics/snowplow/events/megaMenuEvents'
import { useTrackedMenuOpen, trackMegaMenuOpen, trackMegaMenuLinkClick } from 'analytics/snowplow/events/megaMenuEvents'

const Bar = styled(Sheet)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  padding: 0;

  &.disable-sticky {
    position: relative;
  }

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)};
  }
`

const ButtonsContainer = styled.div`
  position: relative;
  display: flex;
  gap: ${rem(4)};
`

const ChipsWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset-inline-end: 0;
    top: 0;
    bottom: 0;
    width: ${rem(48)};
    background: linear-gradient(to left, ${props => props.theme.palette.neutral.surface.normalBackground}, transparent);
    pointer-events: none;
  }

  [dir='rtl'] &::after {
    background: linear-gradient(to right, ${props => props.theme.palette.neutral.surface.normalBackground}, transparent);
  }
`

const ChipsContainer = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(12)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`

interface DesktopMenuConfig {
  label: string
  section: MegaMenuSection
  Menu: React.ComponentType<{ onClose: () => void }>
  alignDropdownWithButton?: boolean
}

function useDesktopMenus(): Array<DesktopMenuConfig> {
  const intl = useIntl()
  return [
    {
      label: intl.formatMessage({
        id: 'sntXOqbSJ9nt3qDJEsV1',
        defaultMessage: 'Shop by Category',
        description: 'Navigation button label for category mega menu in the merchandise shop section',
      }),
      section: 'category',
      Menu: MerchandiseCategoryMegaMenu,
    },
    {
      label: intl.formatMessage({
        id: 'Ubpv1M8TWjb9ks1uTf9g',
        defaultMessage: 'Shop by Brand',
        description: 'Navigation button label for brand mega menu in the merchandise shop section',
      }),
      section: 'brand',
      Menu: MerchandiseBrandsMegaMenu,
    },
    {
      label: intl.formatMessage({
        id: 'qq4JJMZMc3BfjVvuh6td',
        defaultMessage: 'Travel Essentials',
        description: 'Navigation button label for travel essentials mega menu',
      }),
      section: 'travel_essential',
      Menu: MerchandiseTravelEssentialsMegaMenu,
      alignDropdownWithButton: true,
    },
  ]
}

interface DirectLinkConfig {
  label: string
  section: MegaMenuSection
  to: string
}

interface MobileDrawerConfig {
  label: string
  section: MegaMenuSection
  Drawer: React.ComponentType
}

// Map mega menu sections to facet filter names for conditional display
const SECTION_TO_FACET_FILTER: Partial<Record<MegaMenuSection, string>> = {
  free_shipping: 'freeShipping',
  sale: 'onSale',
}

function useDirectLinks(): Array<DirectLinkConfig> {
  const intl = useIntl()
  return [
    {
      label: intl.formatMessage({
        id: 'm4N7F_MT-J2GHHhP-tDG',
        defaultMessage: 'Free Shipping',
        description: 'Navigation link label for free shipping filter in the merchandise shop section',
      }),
      section: 'free_shipping',
      to: '/search/shop?freeShipping=true',
    },
    {
      label: intl.formatMessage({
        id: 'YveExgv03JOMKzhyExdV',
        defaultMessage: 'Sale',
        description: 'Navigation link label for sale filter in the search or listing filters panel',
      }),
      section: 'sale',
      to: '/search/shop?onSale=true',
    },
  ]
}

function useMobileDrawers(): Array<MobileDrawerConfig> {
  const intl = useIntl()
  return [
    {
      label: intl.formatMessage({
        id: 'UhZEReqho8Jaf6wgqYY0',
        defaultMessage: 'Categories',
        description: 'Mobile drawer chip label for the category menu in the merchandise shop section',
      }),
      section: 'category',
      Drawer: MerchandiseCategoryDrawer,
    },
    {
      label: intl.formatMessage({
        id: 'NJFU5pdEFnEYiy1P2pWq',
        defaultMessage: 'Brands',
        description: 'Mobile drawer chip label for the brand menu in the merchandise shop section',
      }),
      section: 'brand',
      Drawer: MerchandiseBrandsDrawer,
    },
    {
      label: intl.formatMessage({
        id: '7Tnf_We9Bx7yJZvci7BK',
        defaultMessage: 'Travel Essentials',
        description: 'Mobile drawer chip label for travel essentials menu',
      }),
      section: 'travel_essential',
      Drawer: MerchandiseTravelEssentialsDrawer,
    },
  ]
}

function TrackedMenuButton({ label, section, Menu, alignDropdownWithButton }: DesktopMenuConfig) {
  const onOpenChange = useTrackedMenuOpen(section)
  return (
    <MerchandiseNavBarButton
      label={label}
      onOpenChange={onOpenChange}
      alignDropdownWithButton={alignDropdownWithButton}
    >
      {(menuOpen, onClose) => menuOpen && <Menu onClose={onClose} />}
    </MerchandiseNavBarButton>
  )
}

function TrackedLinkButton({ label, section, to }: DirectLinkConfig) {
  const handleClick = useCallback(() => trackMegaMenuLinkClick(section), [section])
  return (
    <MerchandiseNavBarButton
      label={label}
      to={to}
      onClick={handleClick}
      showChevron={false}
    />
  )
}

function TrackedLinkChip({ label, section, to }: DirectLinkConfig) {
  const handleClick = useCallback(() => trackMegaMenuLinkClick(section), [section])
  return (
    <MerchandiseNavBarFilterChip
      label={label}
      to={to}
      onClick={handleClick}
    />
  )
}

function TrackedDrawerChip({ label, section, Drawer }: MobileDrawerConfig) {
  const showModal = useContext(ModalContext)
  const handleClick = useCallback(() => {
    trackMegaMenuOpen(section)
    showModal(<Drawer />)
  }, [section, showModal, Drawer])

  return (
    <MerchandiseNavBarFilterChip
      label={label}
      showChevron
      onClick={handleClick}
    />
  )
}

function MerchandiseNavBar() {
  const barRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(barRef, 'sticky')

  const desktopMenus = useDesktopMenus()
  const directLinks = useDirectLinks()
  const mobileDrawers = useMobileDrawers()

  // Fetch facets with no filters to determine which direct links to show
  // Uses cache if available, otherwise makes a single request
  const [facets] = useMerchandiseSearchFacets({})

  // Filter direct links based on available facets
  const visibleDirectLinks = useMemo(() => {
    // If facets haven't loaded yet, show all links
    if (!facets.length) return directLinks

    return directLinks.filter(link => {
      const facetFilter = SECTION_TO_FACET_FILTER[link.section]
      // If no facet mapping, always show the link
      if (!facetFilter) return true
      // Only show if the corresponding facet exists (has items)
      return facets.some(f => f.filter === facetFilter)
    })
  }, [facets, directLinks])

  return <Bar background="normal-background" ref={barRef}>
    <CSSBreakpoint min="desktop">
      <ButtonsContainer>
        {desktopMenus.map((config) => (
          <TrackedMenuButton key={config.section} {...config} />
        ))}
        {visibleDirectLinks.map((config) => (
          <TrackedLinkButton key={config.section} {...config} />
        ))}
      </ButtonsContainer>
    </CSSBreakpoint>

    <CSSBreakpoint max="tablet">
      <ChipsWrapper>
        <ChipsContainer>
          {mobileDrawers.map((config) => (
            <TrackedDrawerChip key={config.section} {...config} />
          ))}
          {visibleDirectLinks.map((config) => (
            <TrackedLinkChip key={config.section} {...config} />
          ))}
        </ChipsContainer>
      </ChipsWrapper>
    </CSSBreakpoint>
  </Bar>
}

export default MerchandiseNavBar
